import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Trash2Fill } from '@lyearn/icons';
import { AlertDialog } from '@lyearn/molecules';
import { useSnackbar } from '@lyearn/ui';
import useResponse from '@/hooks/useResponse';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import useActionItemActions from '@/modules/tasks/features/actionItems/hooks/useActionItemActions';

import { DialogProps } from '../types';

function DeleteTask({ task, onClose, entity, entityId }: DialogProps) {
  const taskToBeDeleted = task as ActionItemFragment;

  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();
  const { actions } = useActionItemActions({
    entity: entity && entityId ? { entityType: entity, entityId } : undefined,
  });

  const onDelete = useCallback(async () => {
    if (!taskToBeDeleted) {
      return;
    }

    setIsFetching(true);
    const data = await actions.onDeleteActionItem(taskToBeDeleted.id);
    handleResponse({
      condition: !!data,
      onSuccess: () => {
        showSnackbar({
          message: t('Task:TaskDeleted'),
          startIcon: <Trash2Fill className="h-20 w-20" fillColorToken="icon-placeholder" />,
        });
        setIsFetching(false);
        onClose();
      },
    });
  }, [actions, handleResponse, showSnackbar, t, taskToBeDeleted, onClose]);

  return (
    <AlertDialog
      header={{
        title: t('Task:DeleteTaskTitle'),
        description: t('Task:DeleteTaskDescription'),
      }}
      primaryAction={{
        appearance: 'danger',
        label: t('Common:Actions.Delete'),
        onClick: onDelete,
        loading: isFetching,
      }}
      onClose={onClose}
    />
  );
}

export default DeleteTask;
